import React from "react";
//#region types
import { FormEnums } from "models/enums";
import {
  FormFieldProps,
  CurrencyFieldExtraProps,
  NumberFormatFieldExtraProps,
} from "models/propTypes/genericFormProps";
//#endregion
//#region components
import useDebounceInput from "../hooks/useDebounceInput";
import usePlaceholder from "../hooks/usePlaceholder";
import ReadOnlyField from "../baseFields/readOnlyField";
import InputField from "../baseFields/inputField";
import { FormControl } from "@mui/material";
//#endregion
import { FormHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import { getIn, useFormikContext } from "formik";
import cssExports from "theme/styles/main.module.scss";

function InputFormField<T extends Record<string, any>>(props: FormFieldProps<T>) {
  const { name, field, errors, value, success, handleOnChangeCustom, handleOnBlurCustom, optional } = props;

  const { values } = useFormikContext<T>();

  const { internalValue, debouncedHandleOnChangeCustom } = useDebounceInput({
    name: name,
    value: value,
    handleOnChangeCustom: handleOnChangeCustom,
    noDebounce: field.noDebounce,
  });

  const getTextFieldType = (type: FormEnums.FormFieldType) => {
    switch (type) {
      case FormEnums.FormFieldType.EMAIL:
        return "email";
      case FormEnums.FormFieldType.PASSWORD:
        return "password";
      default:
        return "text";
    }
  };

  const hasError = !!errors;

  const currencyFieldProps =
    field.type === FormEnums.FormFieldType.CURRENCY ? (field.extraProps as CurrencyFieldExtraProps<T>) : undefined;

  const numberFormatFieldProps =
    field.type === FormEnums.FormFieldType.NUMBER ? (field.extraProps as NumberFormatFieldExtraProps) : undefined;

  const [suffix, setSuffix] = React.useState("");
  const [prefix, setPrefix] = React.useState("");

  React.useEffect(() => {
    const currentSuffix =
      (currencyFieldProps &&
        (currencyFieldProps.currency || currencyFieldProps.currencyReferenceFieldName) &&
        LocalizationHelperInstance.getCurrencySymbol(
          currencyFieldProps.currency ||
            (currencyFieldProps.currencyReferenceFieldName &&
              getIn(values, currencyFieldProps.currencyReferenceFieldName as string))
        )) ||
      (numberFormatFieldProps && numberFormatFieldProps.suffix) ||
      "";
    if (currentSuffix !== suffix) setSuffix(currentSuffix);

    const currentPrefix =
      currencyFieldProps &&
      (currencyFieldProps.isCredit ||
        (currencyFieldProps.isCreditReferenceFieldName &&
          getIn(values, currencyFieldProps.isCreditReferenceFieldName as string)))
        ? "-"
        : "";

    if (currentPrefix !== prefix) setPrefix(currentPrefix);
  }, [values]);

  const [readOnlyValue, setReadOnlyValue] = React.useState<string>();
  React.useEffect(() => {
    if (props.readonly) {
      const currentValue = FormHelper.getFormattedValue<T>(value, values, field);
      if (currentValue !== readOnlyValue) setReadOnlyValue(currentValue);
    }
  }, [values, value]);

  const decimalScale =
    field.type === FormEnums.FormFieldType.CURRENCY ? 2 : (numberFormatFieldProps?.decimalPoints ?? 0);

  const onChangeCustom = (newValue: string, formattedValue: string) => {
    if (field.type === FormEnums.FormFieldType.NUMBER || field.type === FormEnums.FormFieldType.CURRENCY) {
      const castedValue = UtilsHelper.isEmpty(newValue)
        ? undefined
        : field.type === FormEnums.FormFieldType.CURRENCY ||
            (field.type === FormEnums.FormFieldType.NUMBER &&
              !numberFormatFieldProps?.allowLeadingZeros &&
              !numberFormatFieldProps?.format)
          ? Number(newValue)
          : newValue;

      if (value !== castedValue) {
        debouncedHandleOnChangeCustom(name, castedValue);
      }
    } else {
      if (!(UtilsHelper.isEmpty(newValue) && value === undefined)) {
        debouncedHandleOnChangeCustom(name, newValue);
      }
    }

    numberFormatFieldProps &&
      numberFormatFieldProps.formattedValueReferenceFieldName &&
      handleOnChangeCustom(numberFormatFieldProps.formattedValueReferenceFieldName, formattedValue);
  };

  const placeholder = usePlaceholder(field.label, field.placeholder, optional);

  const onCopyToClipBoard = () => {
    navigator.clipboard.writeText(value);
  };

  return props.readonly ? (
    <ReadOnlyField label={field.label} className={cssExports.readOnlyTextField} href={field.href}>
      {!UtilsHelper.isEmpty(prefix) ? `${prefix} ` : ""}
      {readOnlyValue}
    </ReadOnlyField>
  ) : (
    <FormControl fullWidth className={cssExports.inputFormControl}>
      <InputField
        labelPlacement={field.rightAlign ? "right" : "left"}
        label={LocalizationHelperInstance.getFormattedMessage(field.label, field.label)}
        placeholder={placeholder}
        inputBaseProps={{
          value: internalValue ?? "",
          name: name,
          type: getTextFieldType(field.type),
          error: hasError,
          onBlur: handleOnBlurCustom.bind(null, name),
          onFocus: field.onFocusHandler,
          disabled: props.disabled,
          readOnly: props.readonly,
        }}
        icon={field.icon}
        disabled={props.disabled}
        success={success}
        maxLength={field.maxLength}
        isNumericString={
          field.type === FormEnums.FormFieldType.NUMBER || field.type === FormEnums.FormFieldType.CURRENCY
        }
        allowNegative={
          (currencyFieldProps && currencyFieldProps.allowNegative) ||
          (numberFormatFieldProps && numberFormatFieldProps.allowNegative)
        }
        allowLeadingZeros={numberFormatFieldProps && numberFormatFieldProps.allowLeadingZeros}
        format={numberFormatFieldProps ? numberFormatFieldProps.format : undefined}
        decimalPoints={decimalScale}
        fixedDecimalScale={
          field.type === FormEnums.FormFieldType.CURRENCY || numberFormatFieldProps?.fixedDecimalPoints
        }
        prefix={prefix}
        suffix={suffix}
        maxNumber={numberFormatFieldProps?.max}
        onChangeCustom={onChangeCustom}
        onCopyToClipBoard={(field.extraProps as any)?.showCopyButton && onCopyToClipBoard}
        className={field.classes?.input}
        isLoading={props.loading}
      />
    </FormControl>
  );
}

export default InputFormField;
